/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { setCookieLocaleHandler } from "@wf-components/navigation-consumer";

import type { LocalePrefixMode } from "../types/locale.types";

/**
 * Returns true when locale is expressed via query parameter (?lang=) instead of URL path prefix.
 * Controlled by NEXT_PUBLIC_LOCALE_MODE environment variable.
 */
export function isQueryParamLocaleMode(): boolean {
	return process.env.NEXT_PUBLIC_LOCALE_MODE === "never";
}

/**
 * Normalizes a path to ensure it starts with a forward slash
 *
 * @param path - The path to normalize
 * @returns The path with a leading slash
 *
 * @example
 * normalizePath("about") // => "/about"
 * normalizePath("/about") // => "/about"
 */
export function normalizePath(path: string): string {
	return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Generates a locale-aware path based on prefix mode
 *
 * @param path - The path without locale prefix (e.g., "/about")
 * @param locale - Current locale (e.g., "de")
 * @param defaultLocale - Default locale (e.g., "en-US")
 * @param localePrefix - Prefix mode configuration (defaults to "always")
 * @returns The path with or without locale prefix based on configuration
 *
 * @example
 * // With "always" mode (default)
 * getLocalizedPath("/about", "de", "en-US", "always") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "always") // => "/en-US/about"
 *
 * @example
 * // With "as-needed" mode
 * getLocalizedPath("/about", "de", "en-US", "as-needed") // => "/de/about"
 * getLocalizedPath("/about", "en-US", "en-US", "as-needed") // => "/about"
 *
 * @example
 * // With "never" mode
 * getLocalizedPath("/about", "de", "en-US", "never") // => "/about"
 *
 * @example
 * // In navigation components
 * const GlobalNav = () => {
 *   const { getPath } = useLocalePrefix();
 *   return <Link href={getPath("/about", locale)}>About</Link>;
 * };
 *
 * @example
 * // In sitemap generation
 * const sitemapUrl = getLocalizedPath(slug, locale, defaultLocale, "as-needed");
 */
export function getLocalizedPath(
	path: string,
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): string {
	const normalizedPath = normalizePath(path);

	if (localePrefix === "never") {
		return normalizedPath;
	}

	if (localePrefix === "as-needed" && locale === defaultLocale) {
		return normalizedPath;
	}

	return `/${locale}${normalizedPath}`;
}

/**
 * Removes locale prefix from path if present
 *
 * @param path - The path that may contain a locale prefix
 * @param locales - Array of supported locale codes
 * @returns The path without locale prefix
 *
 * @example
 * removeLocaleFromPath("/en-US/about", ["en-US", "de"]) // => "/about"
 * removeLocaleFromPath("/about", ["en-US", "de"]) // => "/about"
 */
export function removeLocaleFromPath(path: string, locales: string[]): string {
	if (!path || !locales?.length) return path || "/";

	const normalizedPath = normalizePath(path);

	for (const locale of locales) {
		if (normalizedPath.startsWith(`/${locale}/`)) {
			return normalizedPath.slice(locale.length + 1);
		}
		if (normalizedPath === `/${locale}`) {
			return "/";
		}
	}

	return normalizedPath;
}

/**
 * Checks if the given locale should have a URL prefix based on the prefix mode
 *
 * @param locale - The locale to check
 * @param defaultLocale - The default locale
 * @param localePrefix - The prefix mode
 * @returns Whether the locale should have a URL prefix
 */
export function shouldHaveLocalePrefix(
	locale: string,
	defaultLocale: string,
	localePrefix: LocalePrefixMode = "always",
): boolean {
	if (localePrefix === "never") {
		return false;
	}

	if (localePrefix === "as-needed") {
		return locale !== defaultLocale;
	}

	return true;
}

/**
 * Checks if Next.js is running in development mode.
 *
 * @returns true if NODE_ENV is "development"
 */
export function isDevMode(): boolean {
	return process.env.NODE_ENV === "development";
}

/**
 * Locale click handler for development mode.
 * Sets cookieSecure to false to allow cookies on http://localhost.epicgames-gamedev.ol.epicgames.net:3000.
 */
export const setCookieLocaleHandlerDev = (e: Event) => setCookieLocaleHandler(e, { cookieSecure: false });

/**
 * Returns the appropriate locale onClick handler based on locale mode.
 * In query param mode, returns setCookieLocaleHandler (dev version in dev mode).
 *
 * @returns The locale click handler function, or undefined if not in query param mode
 *
 * @example
 * const localeOnClick = getLocaleOnClick();
 * const GlobalNav = () => {
 *   return <EpicNavigation localeOnClick={localeOnClick} />;
 * };
 */
export const getLocaleOnClick = (): undefined | ((e: Event) => Promise<void>) => {
	if (!isQueryParamLocaleMode()) {
		return undefined;
	}

	if (isDevMode()) return setCookieLocaleHandlerDev;

	return setCookieLocaleHandler;
};
